import type { RootContent } from "mdast";

/**
 * Minimal shape of a remark-directive node (containerDirective / leafDirective /
 * textDirective). We type it locally rather than pull in mdast-util-directive's
 * module augmentation — we only touch `name`, `attributes`, `children`, and
 * `position`.
 */
export interface DirectiveNode {
  type: "containerDirective" | "leafDirective" | "textDirective";
  name: string;
  attributes?: Record<string, string | null | undefined>;
  children: RootContent[];
  position?: {
    start: { offset?: number };
    end: { offset?: number };
  };
}

const DIRECTIVE_TYPES = new Set([
  "containerDirective",
  "leafDirective",
  "textDirective",
]);

/** Type guard: is this mdast node a remark-directive node? */
export function isDirective(node: { type: string }): node is DirectiveNode {
  return DIRECTIVE_TYPES.has(node.type);
}

/** Normalize directive attributes (drop null/undefined) to a string map. */
export function directiveAttrs(
  node: DirectiveNode,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(node.attributes ?? {})) {
    out[k] = v ?? "";
  }
  return out;
}

/** Quote an attribute value so the directive `{…}` round-trips. */
function quoteValue(v: string): string {
  if (v !== "" && !/[\s"'{}]/.test(v)) return v;
  if (!v.includes('"')) return `"${v}"`;
  return `'${v.replace(/'/g, "")}'`;
}

/** Serialize attrs to a directive attribute block, or "" when empty. */
export function serializeDirectiveAttrs(
  attrs: Record<string, string>,
): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(attrs)) {
    if (!k) continue;
    parts.push(v === "" ? k : `${k}=${quoteValue(v)}`);
  }
  return parts.length ? `{${parts.join(" ")}}` : "";
}

// Opening line of a container directive: `:::name[label]{attrs}` (label/attrs
// optional). Captures the parts so we can rewrite only the `{…}`.
const OPEN_RE = /^(:{3,})([A-Za-z][\w-]*)(\[[^\]]*\])?(\{[^}]*\})?/;

interface DirectiveOpen {
  /** Offset of the first `:` of the opening delimiter. */
  start: number;
  /** Offset just past the opening line (the `\n`, or end of source). */
  lineEnd: number;
  /** Length of the matched `:::name[label]{attrs}` prefix. */
  prefixLen: number;
}

/**
 * Locate a directive's opening line for an attrs-only splice, or null if it
 * can't be edited safely. Bails when the line is prefixed by anything but
 * whitespace (e.g. a `> ` blockquote) — same rule as `fenceAt`.
 */
function directiveOpenAt(
  source: string,
  node: DirectiveNode,
): DirectiveOpen | null {
  const start = node.position?.start.offset;
  if (start == null) return null;
  const lineStart = source.lastIndexOf("\n", start - 1) + 1;
  const indent = source.slice(lineStart, start);
  if (!/^[ \t]*$/.test(indent)) return null;
  const nl = source.indexOf("\n", start);
  const lineEnd = nl === -1 ? source.length : nl;
  const openLine = source.slice(start, lineEnd);
  const m = OPEN_RE.exec(openLine);
  if (!m) return null;
  return { start, lineEnd, prefixLen: m[0].length };
}

/** Whether this directive's attributes can be edited via spliceDirectiveAttrs. */
export function canEditDirective(
  source: string,
  node: DirectiveNode,
): boolean {
  return directiveOpenAt(source, node) !== null;
}

/**
 * Replace just the `{…}` attribute block on a container directive's opening
 * line, leaving the body (and everything else) untouched. Editing a `:::`
 * body inline is unsafe — an inner fence can force the enclosing delimiter to
 * grow — so we only ever rewrite attributes here.
 */
export function spliceDirectiveAttrs(
  source: string,
  node: DirectiveNode,
  attrs: Record<string, string>,
): string {
  const loc = directiveOpenAt(source, node);
  if (!loc) throw new Error("spliceDirectiveAttrs: directive is not editable");
  const { start, lineEnd, prefixLen } = loc;
  const openLine = source.slice(start, lineEnd);
  const m = OPEN_RE.exec(openLine);
  if (!m) throw new Error("spliceDirectiveAttrs: opening line did not match");
  const [, colons, name, label = ""] = m;
  const rebuilt =
    colons + name + label + serializeDirectiveAttrs(attrs) + openLine.slice(prefixLen);
  return source.slice(0, start) + rebuilt + source.slice(lineEnd);
}

/**
 * Unwrap a directive: drop only its `:::` wrapper (opening line + closing
 * delimiter) and keep the body in place. A `:::sidebyside` with two embeds
 * unwraps to those two embeds, back to stacking. The body is taken verbatim
 * from the first child's start offset to the last child's end offset, so inner
 * embeds — and their exact source — survive untouched. The seam is normalized
 * to a single blank-line separator. Bails on an indented/quoted directive,
 * same rule as the attrs splice.
 */
export function unwrapDirective(source: string, node: DirectiveNode): string {
  const open = directiveOpenAt(source, node);
  const end = node.position?.end.offset;
  if (!open || end == null) {
    throw new Error("unwrapDirective: directive is not editable");
  }
  const lineStart = source.lastIndexOf("\n", open.start - 1) + 1;
  const kids = node.children;
  const bodyStart = kids[0]?.position?.start.offset;
  const bodyEnd = kids[kids.length - 1]?.position?.end.offset;
  const body =
    bodyStart != null && bodyEnd != null
      ? source.slice(bodyStart, bodyEnd)
      : "";
  const before = source.slice(0, lineStart).replace(/\r?\n+$/, "");
  const after = source.slice(end).replace(/^\r?\n+/, "");
  let result = [before, body, after].filter((s) => s !== "").join("\n\n");
  if (source.endsWith("\n") && !result.endsWith("\n")) result += "\n";
  return result;
}
